import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getRequests } from '../services/api';
import { getCategoryLabel, decodeHtmlEntities } from '../utils/categoryUtils';
import { getUrgencyLabel, getUrgencyColor } from '../utils/urgencyUtils';
import { formatBudgetRange } from '../utils/budgetUtils';

interface QuoteRequest {
  id: number;
  title: string;
  description: string;
  category: string;
  urgency: string;
  budget_min: string | null;
  budget_max: string | null;
  city?: string;
  location: string;
  status: string;
  created_at: string;
  customer_name: string;
}

const Requests: React.FC = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<QuoteRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if user is a customer - redirect them away
    const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!) : null;
    if (user?.user_type === 'customer') {
      navigate('/new-request');
      return;
    }
    fetchRequests();
  }, [navigate]);

  const fetchRequests = async () => {
    try {
      const response = await getRequests();
      setRequests(response.data.requests);
    } catch (err: any) {
      setError('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={styles.loading}>Loading requests...</div>;
  if (error) return <div style={styles.error}>{error}</div>;

  return (
    <div style={styles.container}>
      <h1>📋 Open Quote Requests</h1>
      <p style={styles.subtitle}>Browse requests and submit your best quote!</p>

      {requests.length === 0 ? (
        <div style={styles.empty}>
          <p>No open requests right now. Check back soon!</p>
        </div>
      ) : (
        <div style={styles.grid}>
          {requests.map((request) => (
            <div key={request.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <span style={{...styles.urgency, backgroundColor: getUrgencyColor(request.urgency)}}>
                  {getUrgencyLabel(request.urgency)}
                </span>
                <span style={styles.category}>{getCategoryLabel(request.category)}</span>
              </div>

              <h3 style={styles.title}>{request.title}</h3>
             <p style={styles.description}>{decodeHtmlEntities(request.description).substring(0, 150)}...</p>

              <div style={styles.details}>
                <p>📍 {request.city || request.location || 'Not specified'}</p>
                <p>💰 {formatBudgetRange(request.budget_min, request.budget_max)}</p>
                <p>👤 {request.customer_name}</p>
              </div>

              <Link to={`/request/${request.id}`} style={styles.viewButton}>
                View Details & Submit Quote
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '40px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  subtitle: {
    color: '#7f8c8d',
    marginBottom: '30px',
  },
  loading: {
    textAlign: 'center',
    padding: '50px',
    fontSize: '18px',
  },
  error: {
    textAlign: 'center',
    padding: '50px',
    color: '#e74c3c',
  },
  empty: {
    textAlign: 'center',
    padding: '50px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '20px',
  },
  card: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '10px',
  },
  urgency: {
    padding: '4px 10px',
    borderRadius: '15px',
    color: 'white',
    fontSize: '12px',
  },
  category: {
    padding: '4px 10px',
    backgroundColor: '#ecf0f1',
    borderRadius: '15px',
    fontSize: '12px',
  },
  title: {
    marginBottom: '10px',
    color: '#2c3e50',
  },
  description: {
    color: '#7f8c8d',
    marginBottom: '15px',
  },
  details: {
    borderTop: '1px solid #ecf0f1',
    paddingTop: '15px',
    marginBottom: '15px',
  },
  viewButton: {
    display: 'block',
    textAlign: 'center',
    padding: '12px',
    backgroundColor: '#3498db',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '5px',
  },
};

export default Requests;